import { useContext, useEffect, useState } from 'react';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import { selectActiveVersion } from '@/redux/selectors/titleSelectors';
import { isActiveVersionAVersion, TitleMetadata } from '@/redux/slices/slice_types';
import { Box, Grid, lighten, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useAppSelector } from '@/redux/hooks';
import axios from '@/_utils/axios';
import styled from '@emotion/styled';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { ConfigContext } from '@/contextAPI/configContext';
import TitleDetailMetadataButtons from '../../TitleDetailMetadata/TitleDetailMetadataButtons';

const TitleDetailMetadataSchema = () => {
  const activeVersion = useAppSelector(selectActiveVersion);
  if (!isActiveVersionAVersion(activeVersion)) return null;

  const [metadataByLanguage, setMetadataByLanguage] = useState<{ [key: string]: TitleMetadata }>({});
  const [editMode, setEditMode] = useState<boolean>(false);
  const [data, setData] = useState<any>({});
  const [originalData, setOriginalData] = useState<any>({});
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [fetchingMetadata, setFetchingMetadata] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const enableMetadataUpdate = useContext(ConfigContext)?.titleConfig?.enableMetadataUpdate;

  // only set up debounced updates if editing is enabled
  const debouncedSetData = enableMetadataUpdate ? debounce(setData, 600) : undefined;

  const setInitialData = (newData: any, language?: string) => {
    setData(newData);
    setOriginalData(newData);
    setDataLoaded(true);
    if (language) {
      setSelectedLanguage(language);
    }
  };

  const fetchMetadata = (versionId: string | number, language: string): Promise<any> => {
    setFetchingMetadata(true);
    return axios
      .get(`/inventory/titles/${versionId}/metadata/${language}/`)
      .then((res) => {
        if (!res.data) {
          throw new Error('No metadata found');
        } else {
          return res.data?.data;
        }
      })
      .catch((error) => {
        console.error('Error fetching metadata:', error);
        return null;
      })
      .finally(() => setFetchingMetadata(false));
  };

  useEffect(() => {
    return () => {
      debouncedSetData?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!activeVersion?.id) {
      if (dataLoaded) {
        setDataLoaded(false);
      }
      return;
    }

    // don't reload if we already have data for this version
    if (dataLoaded && data && Object.keys(data).length > 0) {
      if (activeVersion.metadata_values && Object.keys(activeVersion.metadata_values).length > 0) {
        const hasNonEmptyValues = Object.values(activeVersion.metadata_values).some(
          (value) => value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)
        );
        if (hasNonEmptyValues && JSON.stringify(activeVersion.metadata_values) !== JSON.stringify(originalData)) {
          setInitialData(activeVersion.metadata_values, activeVersion.language);
        }
      }
      return;
    }

    if (activeVersion.metadata_values && Object.keys(activeVersion.metadata_values).length > 0) {
      const hasNonEmptyValues = Object.values(activeVersion.metadata_values).some(
        (value) => value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)
      );
      if (hasNonEmptyValues) {
        setInitialData(activeVersion.metadata_values, activeVersion.language);
        return;
      }
    }

    // fetch from api if we don't have metadata_values
    const languageToUse = activeVersion.language || (Array.isArray(activeVersion.metadata_languages) ? activeVersion.metadata_languages[0] : null);
    if (languageToUse && activeVersion.id) {
      setSelectedLanguage(languageToUse);
      fetchMetadata(activeVersion.id, languageToUse).then((fetchedData) => {
        if (fetchedData?.metadata_values) {
          setInitialData(fetchedData.metadata_values, languageToUse);
        } else {
          setDataLoaded(true);
        }
      });
      return;
    }

    if (Array.isArray(activeVersion.metadata_languages)) {
      const metadataByLang: { [key: string]: any } = {};

      if (activeVersion.language && activeVersion.metadata_values) {
        metadataByLang[activeVersion.language] = activeVersion.metadata_values;
      }

      activeVersion.metadata_languages.forEach((lang: string) => {
        if (!metadataByLang[lang]) {
          metadataByLang[lang] = {};
        }
      });

      setMetadataByLanguage(metadataByLang);

      const languageToSelect = activeVersion.language || activeVersion.metadata_languages[0];
      if (languageToSelect) {
        const dataForLanguage = metadataByLang[languageToSelect] || {};
        setInitialData(dataForLanguage, languageToSelect);
      }
    } else if (activeVersion.metadata_languages && typeof activeVersion.metadata_languages === 'object') {
      setMetadataByLanguage(activeVersion.metadata_languages || {});

      const firstLanguageKey = Object.keys(activeVersion.metadata_languages || {})[0];
      if (firstLanguageKey) {
        const initialData = activeVersion.metadata_languages[firstLanguageKey];
        if (typeof initialData === 'object' && initialData !== null) {
          setInitialData(initialData, firstLanguageKey);
        } else {
          setInitialData({}, firstLanguageKey);
        }
      }
    } else {
      setDataLoaded(true);
    }
  }, [activeVersion?.id, activeVersion?.metadata_values, activeVersion?.metadata_languages, activeVersion?.language, dataLoaded]);

  useEffect(() => {
    if (selectedLanguage && metadataByLanguage[selectedLanguage]) {
      const languageData = metadataByLanguage[selectedLanguage];
      if (languageData && Object.keys(languageData).length > 0) {
        setData(languageData);
      }
    }
  }, [selectedLanguage, metadataByLanguage]);

  const handleSubmit = () => {
    axios
      .post(`/inventory/titles/${activeVersion.id}/metadata/${selectedLanguage}/`, { data })
      .then((response) => {
        console.log('Metadata submitted successfully:', response.data);
      })
      .catch((error) => {
        console.error('Error submitting metadata:', error);
      });
  };

  const handleCancel = () => {
    setEditMode(false);
    setData(originalData);
  };

  if (!activeVersion.metadata_schema && !activeVersion.metadata_languages?.length && !activeVersion.metadata_values) {
    return null;
  }

  // if metadata updates are disabled, just show a simple read-only view
  if (!enableMetadataUpdate) {
    return (
      <VersionBox>
        <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem', display: 'flex', mb: 3 }}>
          <PolarisIcon height={26} icon="iconamoon:arrow-bottom-right-3-square-thin" style={{ marginRight: 5 }} />
          <span>{activeVersion.name}</span>
        </Typography>
        <SchemaForm
          schema={activeVersion.metadata_schema}
          formData={data || metadataByLanguage[selectedLanguage] || {}}
          readonly
          formContext={{ rowSpacing: 3, columnSpacing: 4 }}
        />
      </VersionBox>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <VersionBox style={{ opacity: fetchingMetadata ? 0.5 : 1 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem', display: 'flex' }}>
            <PolarisIcon height={26} icon="iconamoon:arrow-bottom-right-3-square-thin" style={{ marginRight: 5 }} />
            <span>{activeVersion.name}</span>
          </Typography>
          <div>
            <TitleDetailMetadataButtons editMode={editMode} setEditMode={setEditMode} handleSubmit={handleSubmit} handleCancel={handleCancel} />
          </div>
        </Grid>
        <SchemaForm
          schema={activeVersion.metadata_schema}
          formData={data || metadataByLanguage[selectedLanguage] || {}}
          readonly={!editMode}
          onChange={(formChangeData) => {
            // only update when editing to prevent initial data override
            if (editMode && debouncedSetData) {
              debouncedSetData(formChangeData.formData);
            }
          }}
          formContext={{ rowSpacing: 3, columnSpacing: 4 }}
        />{' '}
        {editMode && (
          <Box sx={{ mt: 3 }}>
            <TitleDetailMetadataButtons editMode={editMode} setEditMode={setEditMode} handleSubmit={handleSubmit} handleCancel={handleCancel} />
          </Box>
        )}
      </VersionBox>
    </div>
  );
};

export default TitleDetailMetadataSchema;

const VersionBox = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => lighten(theme.palette.background.default, 0.03)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;
